// Scan the next ~24h of hourly forecast for the best 2-3 hour outdoor block.

export interface HourlyHour {
  dt: number;
  temp: number;
  feels_like?: number;
  wind_speed?: number;
  pop?: number; // 0..1
  rain?: { "1h"?: number };
  snow?: { "1h"?: number };
  weather?: { id: number; icon: string; main: string }[];
}

export interface BestWindow {
  startTs: number;
  endTs: number;
  hours: number;
  score: number; // 0-10
  reason: string;
  avgTempC: number;
  maxPop: number;
  avgWindMs: number;
}

function comfortAt(h: HourlyHour): number {
  const t = h.feels_like ?? h.temp;
  const wind = h.wind_speed ?? 0;
  const pop = h.pop ?? 0;
  const precip =
    (h.rain?.["1h"] ?? 0) + (h.snow?.["1h"] ?? 0);

  // Temperature comfort: peak around 18°C feels-like.
  const tempScore = Math.max(0, 10 - Math.abs(t - 18) * 0.5);
  // Wind: every m/s above 4 chips off.
  const windScore = Math.max(0, 10 - Math.max(0, wind - 4) * 0.8);
  // Precipitation: pop and intensity.
  const popScore = 10 - pop * 9;
  const precipScore = Math.max(0, 10 - precip * 5);

  return (tempScore * 1 + windScore * 0.7 + popScore * 1.2 + precipScore * 1.1) / 4;
}

export function findBestWindow(
  hourly: HourlyHour[] | undefined,
  options: { minHours?: number; maxHours?: number; lookahead?: number } = {},
): BestWindow | null {
  if (!hourly || hourly.length < 2) return null;
  const minHours = options.minHours ?? 2;
  const maxHours = options.maxHours ?? 3;
  const lookahead = Math.min(options.lookahead ?? 18, hourly.length);

  // Compute per-hour scores once.
  const scores = hourly.slice(0, lookahead).map(comfortAt);

  let best: BestWindow | null = null;

  for (let span = maxHours; span >= minHours; span--) {
    for (let i = 0; i + span <= scores.length; i++) {
      const slice = scores.slice(i, i + span);
      const avg = slice.reduce((a, b) => a + b, 0) / slice.length;
      const hours = hourly.slice(i, i + span);
      const maxPop = Math.max(0, ...hours.map((h) => h.pop ?? 0));
      const avgTemp = hours.reduce((a, b) => a + (b.feels_like ?? b.temp), 0) / hours.length;
      const avgWind =
        hours.reduce((a, b) => a + (b.wind_speed ?? 0), 0) / hours.length;

      const candidate: BestWindow = {
        startTs: hours[0].dt,
        endTs: hours[hours.length - 1].dt + 3600,
        hours: span,
        score: Math.round(avg * 10) / 10,
        reason: describe(avg, maxPop, avgWind, avgTemp),
        avgTempC: Math.round(avgTemp * 10) / 10,
        maxPop: Math.round(maxPop * 100),
        avgWindMs: Math.round(avgWind * 10) / 10,
      };

      if (!best || candidate.score > best.score) best = candidate;
    }
  }

  return best;
}

function describe(score: number, maxPop: number, wind: number, temp: number): string {
  const bits: string[] = [];
  if (score >= 8.5) bits.push("ideal");
  else if (score >= 7) bits.push("good");
  else if (score >= 5) bits.push("manageable");
  else bits.push("rough");

  if (maxPop > 0.5) bits.push("rain risk");
  else if (maxPop > 0.2) bits.push("light rain risk");
  if (wind > 8) bits.push("breezy");
  if (temp < 5) bits.push("chilly");
  if (temp > 28) bits.push("hot");

  return bits.join(" · ");
}

export function formatWindowRange(startTs: number, endTs: number): string {
  const fmt = (ts: number) =>
    new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${fmt(startTs)} – ${fmt(endTs)}`;
}
